import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { notificationAPI } from '../api/client';
import { track } from '../utils/analytics';
import BottomSheet from './BottomSheet';

// ─── 타입별 아이콘 ───
const TypeIcon = ({ type }) => {
  if (type === 'reservation') {
    return (
      <div className="w-8 h-8 rounded-full bg-tayo/10 flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-tayo" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8m-8 4h4m-2 6v2m4-2v2M6 21h12a2 2 0 002-2V7a4 4 0 00-4-4H8a4 4 0 00-4 4v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }
  if (type === 'admin') {
    return (
      <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 3.296a2.13 2.13 0 013.32 0l7.05 8.78c.96 1.196.1 2.924-1.39 2.924H4.68c-1.49 0-2.35-1.728-1.39-2.924l7.05-8.78zM12 17.5v.01" />
        </svg>
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
      <svg className="w-4 h-4 text-sub-text" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
    </div>
  );
};

// ─── 빈 상태 일러스트 ───
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-12 px-6">
    <div className="w-16 h-16 rounded-full bg-snow flex items-center justify-center mb-4">
      <svg className="w-8 h-8 text-border" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
    </div>
    <p className="text-sm font-medium text-charcoal">알림이 없어요</p>
    <p className="text-xs text-sub-text mt-1">새로운 소식이 오면 여기에 표시됩니다</p>
  </div>
);

// ─── 시간 그루핑 유틸 ───
function groupByTime(notifications) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart - 86400000);
  const weekStart = new Date(todayStart - 6 * 86400000);

  const groups = [];
  let currentGroup = null;

  for (const n of notifications) {
    const d = new Date(n.created_at);
    let label;
    if (d >= todayStart) label = '오늘';
    else if (d >= yesterdayStart) label = '어제';
    else if (d >= weekStart) label = '이번 주';
    else label = '이전';

    if (!currentGroup || currentGroup.label !== label) {
      currentGroup = { label, items: [] };
      groups.push(currentGroup);
    }
    currentGroup.items.push(n);
  }
  return groups;
}

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return '방금';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}일 전`;
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

// ─── 알림 아이템 ───
const NotificationItem = ({ n, index, onRead }) => {
  const typeBar = {
    reservation: 'bg-tayo',
    admin: 'bg-amber-500',
  };

  return (
    <div
      onClick={() => !n.is_read && onRead(n.id)}
      className={`
        relative flex items-start gap-3 px-4 py-3.5 transition-all duration-200 cursor-pointer
        animate-notif-in
        ${n.is_read ? 'bg-white hover:bg-snow/80' : 'bg-tayo/[0.03] hover:bg-tayo/[0.06]'}
      `}
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      {/* 미읽음 컬러 바 */}
      {!n.is_read && (
        <div className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full ${typeBar[n.type] || 'bg-sub-text'}`} />
      )}

      <TypeIcon type={n.type} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-[13px] leading-tight truncate ${n.is_read ? 'text-charcoal' : 'text-charcoal font-semibold'}`}>
            {n.title}
          </p>
          <span className="text-[11px] text-sub-text whitespace-nowrap flex-shrink-0">
            {formatTime(n.created_at)}
          </span>
        </div>
        <p className="text-xs text-sub-text mt-1 line-clamp-2 leading-relaxed">{n.body}</p>
      </div>
    </div>
  );
};

// ─── 알림 목록 콘텐츠 (공유) ───
const NotificationContent = ({ loading, groups, hasMore, onLoadMore, onRead, onMarkAllRead, unreadCount }) => (
  <>
    {/* 헤더 */}
    <div className="px-4 py-3.5 border-b border-border flex items-center justify-between">
      <h3 className="text-[15px] font-bold text-charcoal">알림</h3>
      {unreadCount > 0 && (
        <button
          onClick={onMarkAllRead}
          className="text-xs text-tayo hover:text-tayo-dark font-medium transition-colors active:scale-95"
        >
          모두 읽음
        </button>
      )}
    </div>

    {/* 목록 */}
    <div className="flex-1 overflow-y-auto overscroll-contain">
      {loading ? (
        <div className="flex items-center justify-center py-14">
          <div className="w-6 h-6 border-2 border-tayo/20 border-t-tayo rounded-full animate-spin" />
        </div>
      ) : groups.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {groups.map((group) => (
            <div key={group.label}>
              {/* 그룹 헤더 */}
              <div className="sticky top-0 z-10 px-4 py-2 bg-snow/90 backdrop-blur-sm border-b border-border/50">
                <span className="text-[11px] font-semibold text-sub-text uppercase tracking-wider">
                  {group.label}
                </span>
              </div>
              {/* 아이템 */}
              <div className="divide-y divide-border/40">
                {group.items.map((n, i) => (
                  <NotificationItem key={n.id} n={n} index={i} onRead={onRead} />
                ))}
              </div>
            </div>
          ))}
          {hasMore && (
            <button
              onClick={onLoadMore}
              className="w-full py-3.5 text-xs text-tayo hover:text-tayo-dark font-medium transition-colors border-t border-border/50 active:bg-snow"
            >
              이전 알림 더 보기
            </button>
          )}
        </>
      )}
    </div>
  </>
);

// ─── 메인 컴포넌트 ───
export default function NotificationBell() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [extraNotifications, setExtraNotifications] = useState([]); // loadMore로 추가된 항목
  const [hasMore, setHasMore] = useState(false);
  const [shaking, setShaking] = useState(false);
  const prevCountRef = useRef(0);
  const panelRef = useRef(null);

  const userId = user?.userId;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // ─── 미읽음 카운트 (useQuery, 30초 폴링) ───
  const { data: unreadData } = useQuery({
    queryKey: ['notif-unread', userId],
    queryFn: () => notificationAPI.unreadCount(userId),
    enabled: !!userId,
    refetchInterval: 30000,
    staleTime: 0,
    select: (res) => (res.success ? res.count : 0),
  });
  const unreadCount = unreadData ?? 0;

  // 새 알림 도착 시 벨 흔들림
  useEffect(() => {
    if (unreadCount > prevCountRef.current && prevCountRef.current !== 0) {
      setShaking(true);
      const t = setTimeout(() => setShaking(false), 700);
      return () => clearTimeout(t);
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount]);

  // FCM 포그라운드 수신
  useEffect(() => {
    const handler = () => {
      setShaking(true);
      setTimeout(() => setShaking(false), 700);
      queryClient.invalidateQueries({ queryKey: ['notif-unread', userId] });
    };
    window.addEventListener('fcm-message', handler);
    return () => window.removeEventListener('fcm-message', handler);
  }, [queryClient, userId]);

  // ─── 알림 목록 (useQuery, 패널 열릴 때만) ───
  const { data: listData, isFetching: listFetching } = useQuery({
    queryKey: ['notif-list', userId],
    queryFn: () => notificationAPI.list(userId, { limit: 20, offset: 0 }),
    enabled: !!userId && isOpen,
    staleTime: 0,
    select: (res) => (res.success ? { notifications: res.notifications, total: res.total } : { notifications: [], total: 0 }),
  });

  // 패널이 열릴 때마다 extraNotifications 리셋
  useEffect(() => {
    if (isOpen) {
      setExtraNotifications([]);
    }
  }, [isOpen]);

  // hasMore 계산: listData 또는 extraNotifications 변경 시 갱신
  useEffect(() => {
    if (!listData) return;
    const baseCount = listData.notifications.length;
    const total = listData.total;
    setHasMore(baseCount + extraNotifications.length < total);
  }, [listData, extraNotifications]);

  // 표시할 알림 목록 (초기 20개 + loadMore 추가분)
  const notifications = listData ? [...listData.notifications, ...extraNotifications] : [];
  const loading = listFetching && notifications.length === 0;

  // ─── 읽음 처리 뮤테이션 ───
  const markReadMutation = useMutation({
    mutationFn: ({ notifId }) => notificationAPI.markRead(userId, notifId ?? null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notif-unread', userId] });
      queryClient.invalidateQueries({ queryKey: ['notif-list', userId] });
      setExtraNotifications([]);
    },
  });

  // ─── 패널 열기 ───
  const openPanel = () => {
    track('notification-opened');
    setIsOpen(true);
  };

  const loadMore = async () => {
    try {
      const res = await notificationAPI.list(userId, { limit: 20, offset: notifications.length });
      if (res.success) {
        setExtraNotifications((prev) => [...prev, ...res.notifications]);
        const newTotal = (listData?.notifications.length ?? 0) + extraNotifications.length + res.notifications.length;
        setHasMore(newTotal < res.total);
      }
    } catch {
      // silent
    }
  };

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      openPanel();
    }
  };

  // ─── 읽음 처리 ───
  const markAllRead = () => {
    if (unreadCount === 0) return;
    markReadMutation.mutate({ notifId: undefined });
  };

  const markOneRead = (notifId) => {
    markReadMutation.mutate({ notifId });
  };

  // ─── 바깥 클릭 (데스크탑) ───
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, isMobile]);

  const groups = groupByTime(notifications);

  const contentProps = {
    loading,
    groups,
    hasMore,
    onLoadMore: loadMore,
    onRead: markOneRead,
    onMarkAllRead: markAllRead,
    unreadCount,
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* ─── 벨 아이콘 ─── */}
      <button
        onClick={handleToggle}
        className={`relative p-2 rounded-xl text-sub-text hover:text-charcoal hover:bg-snow transition-all active:scale-90 ${shaking ? 'animate-bell-shake' : ''}`}
        aria-label="알림"
      >
        <svg className="w-[22px] h-[22px]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unreadCount > 0 && (
          <span
            key={unreadCount}
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-coral text-white text-[10px] font-bold rounded-full px-1 animate-badge-pop shadow-sm"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* ─── 데스크탑 드롭다운 (sm+) ─── */}
      {isOpen && !isMobile && (
        <div className="absolute right-0 top-full mt-2 w-[360px] max-h-[75vh] bg-white rounded-2xl shadow-xl border border-border/80 overflow-hidden z-50 flex flex-col animate-dropdown-open">
          <NotificationContent {...contentProps} />
        </div>
      )}

      {/* ─── 모바일 바텀시트 (<sm) ─── */}
      {isMobile && (
        <BottomSheet
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          expandable
          minHeight="40vh"
          maxHeight="85vh"
          expandedMinHeight="85vh"
          expandedMaxHeight="95vh"
          zIndex={9999}
        >
          <NotificationContent {...contentProps} />
        </BottomSheet>
      )}
    </div>
  );
}
